import { sanitizeHealthieDate } from '@web-app/utils/date-time.utils';
import type { DaySlotDetail } from '@web-app/utils/scheduling/day-slot-status';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

const LOCAL_SLOT_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

/** Provider-local bookable slot ("yyyy-MM-dd HH:mm:ss"). */
export function isLocalSlotString(value: string): boolean {
  return LOCAL_SLOT_RE.test(value);
}

function parseSlotOrHealthieDatetime(value: string, timezone: string): number {
  if (isLocalSlotString(value)) {
    return fromZonedTime(value.replace(' ', 'T'), timezone).getTime();
  }
  return sanitizeHealthieDate(value).getTime();
}

/** Provider wall-clock datetime → offset-qualified datetime for Healthie writes. */
export function toHealthieBookingDatetime(
  datetime: string,
  timezone: string,
): string {
  const wallClock = datetime.slice(0, 19).replace(' ', 'T');
  const instant = fromZonedTime(wallClock, timezone);
  return formatInTimeZone(instant, timezone, "yyyy-MM-dd'T'HH:mm:ssxxx");
}

/** Healthie/start datetime → provider-local slot string for the bookable list. */
export function toLocalSlotString(
  datetime: string,
  timezone: string,
): string {
  return formatInTimeZone(
    sanitizeHealthieDate(datetime),
    timezone,
    'yyyy-MM-dd HH:mm:ss',
  );
}

/** True when two slot/Healthie strings refer to the same instant in `timezone`. */
export function slotsRepresentSameInstant(
  a: string,
  b: string,
  timezone: string,
): boolean {
  if (!a || !b) return false;
  return (
    parseSlotOrHealthieDatetime(a, timezone) ===
    parseSlotOrHealthieDatetime(b, timezone)
  );
}

/** Wall-clock "HH:MM" from a slot string ("YYYY-MM-DD HH:MM:00"). */
function slotWallTime(slot: string): string {
  return slot.slice(11, 16);
}

export function isAllowedTimeSlot(
  slot: string,
  timeSlots: readonly string[],
  existingStart: string | null | undefined,
  timezone: string,
): boolean {
  return (
    timeSlots.includes(slot) ||
    (!!existingStart && slotsRepresentSameInstant(slot, existingStart, timezone))
  );
}

/**
 * Scroll target index: top (0) for future dates, else the first slot at-or-after
 * `nowHhmm` for today (or the last slot when now is past every slot).
 */
export function scrollTargetIndex(
  slots: string[],
  nowHhmm: string,
  isToday: boolean,
): number {
  if (!isToday) return 0;
  const idx = slots.findIndex((slot) => slotWallTime(slot) >= nowHhmm);
  return idx === -1 ? Math.max(slots.length - 1, 0) : idx;
}

/**
 * Where a day's slot list opens scrolled: the first slot inside the provider's
 * availability, else the first slot from `fallbackHhmm` (a working-day start),
 * else the top. Slots earlier than now are already dropped server-side, so for
 * today this lands at or after the current time without a clock check.
 *
 * Booked slots inside a window are indistinguishable from booked slots outside
 * it, so a window whose first slot is taken anchors at the next open one.
 */
export function listAnchorIndex(
  details: readonly DaySlotDetail[],
  fallbackHhmm = '07:00',
): number {
  const firstOpen = details.findIndex((d) => d.status === 'open');
  if (firstOpen !== -1) return firstOpen;
  const fallback = details.findIndex(
    (d) => slotWallTime(d.slot) >= fallbackHhmm,
  );
  return Math.max(fallback, 0);
}
